import type {
  EditGraph,
  EditOp,
  NormalizedRect,
  SfxSlotId,
} from "../contracts/edit-graph.js";
import {
  EDIT_GRAPH_SCHEMA_VERSION,
  EDIT_GRAPH_SCHEMA_VERSION_V1,
  SFX_SLOT_ID_SET,
} from "../contracts/edit-graph.js";
import type { CaptionTokenRole, SourcePack } from "../contracts/source-pack.js";
import {
  CAPTION_REVEAL_MODES,
  CAPTION_STYLES,
  type CaptionAuthoredStyle,
  type CaptionRevealMode,
} from "../edit/caption-presentation.js";
import {
  isSupportedEditGraphSchemaVersion,
  migrateEditGraphToCurrent,
} from "../edit/migrate-edit-graph.js";
import { stickerIntervalCoveredByAllowedBeats } from "../edit/sticker-bounds.js";
import {
  requireArray,
  requireBoolean,
  requireInt,
  requireMilli,
  requireNonEmptyString,
  requireObject,
  requireStringArray,
  uniqueIds,
  ValidationError,
} from "./helpers.js";

const SOUND_ROLES = new Set(["bed", "sting", "ambience", "silence"]);
const CUT_MODES = new Set(["keep", "omit", "trim"]);

function packSourceEndTick(beats: { endTick: number }[]): number {
  return beats.reduce((max, b) => Math.max(max, b.endTick), 0);
}

function validateRect(path: string, raw: unknown): NormalizedRect {
  const crop = requireObject(path, raw);
  const rect = {
    xMilli: requireMilli(`${path}.xMilli`, crop.xMilli, 0, 1000),
    yMilli: requireMilli(`${path}.yMilli`, crop.yMilli, 0, 1000),
    wMilli: requireMilli(`${path}.wMilli`, crop.wMilli, 1, 1000),
    hMilli: requireMilli(`${path}.hMilli`, crop.hMilli, 1, 1000),
  };
  if (rect.xMilli + rect.wMilli > 1000 || rect.yMilli + rect.hMilli > 1000) {
    throw new ValidationError(path, "crop exceeds authored camera space");
  }
  return rect;
}

function requireNullableString(path: string, raw: unknown): string | null {
  if (raw === null) return null;
  return requireNonEmptyString(path, raw);
}

function validateOpShape(opRaw: unknown, index: number): EditOp {
  const path = `ops[${index}]`;
  const op = requireObject(path, opRaw);
  const kind = requireNonEmptyString(`${path}.kind`, op.kind);
  const opId = requireNonEmptyString(`${path}.opId`, op.opId);

  switch (kind) {
    case "cut": {
      const mode = requireNonEmptyString(`${path}.mode`, op.mode);
      if (!CUT_MODES.has(mode)) {
        throw new ValidationError(`${path}.mode`, "invalid cut mode");
      }
      const beatId = requireNonEmptyString(`${path}.beatId`, op.beatId);
      if (mode === "trim") {
        const inTick = requireInt(`${path}.inTick`, op.inTick, 0);
        const outTick = requireInt(`${path}.outTick`, op.outTick, inTick + 1);
        return { kind: "cut", opId, beatId, mode: "trim", inTick, outTick };
      }
      return { kind: "cut", opId, beatId, mode: mode as "keep" | "omit" };
    }
    case "reorder": {
      const beatOrder = requireStringArray(`${path}.beatOrder`, op.beatOrder);
      if (beatOrder.length === 0) {
        throw new ValidationError(`${path}.beatOrder`, "empty");
      }
      return { kind: "reorder", opId, beatOrder };
    }
    case "frame": {
      const subjectRaw = op.subjectEntityId;
      let subjectEntityId: string | null = null;
      if (subjectRaw !== null && subjectRaw !== undefined) {
        subjectEntityId = requireNonEmptyString(`${path}.subjectEntityId`, subjectRaw);
      } else if (subjectRaw === undefined) {
        throw new ValidationError(`${path}.subjectEntityId`, "expected string or null");
      }
      return {
        kind: "frame",
        opId,
        beatId: requireNonEmptyString(`${path}.beatId`, op.beatId),
        subjectEntityId,
        crop: validateRect(`${path}.crop`, op.crop),
        zoomMilli: requireMilli(`${path}.zoomMilli`, op.zoomMilli, 100, 5000),
      };
    }
    case "text": {
      const tokenIds = requireStringArray(`${path}.tokenIds`, op.tokenIds);
      const startTick = requireInt(`${path}.startTick`, op.startTick, 0);
      const endTick = requireInt(`${path}.endTick`, op.endTick, startTick + 1);
      const textOp: Extract<EditOp, { kind: "text" }> = {
        kind: "text",
        opId,
        captionId: requireNonEmptyString(`${path}.captionId`, op.captionId),
        tokenIds,
        startTick,
        endTick,
      };
      if (op.style !== undefined) {
        const style = requireNonEmptyString(`${path}.style`, op.style);
        if (!CAPTION_STYLES.has(style as CaptionAuthoredStyle)) {
          throw new ValidationError(`${path}.style`, `invalid caption style "${style}"`);
        }
        textOp.style = style as CaptionAuthoredStyle;
      }
      if (op.revealMode !== undefined) {
        const revealMode = requireNonEmptyString(`${path}.revealMode`, op.revealMode);
        if (!CAPTION_REVEAL_MODES.has(revealMode as CaptionRevealMode)) {
          throw new ValidationError(
            `${path}.revealMode`,
            `invalid caption revealMode "${revealMode}"`,
          );
        }
        textOp.revealMode = revealMode as CaptionRevealMode;
      }
      if (op.scaleMilli !== undefined) {
        textOp.scaleMilli = requireMilli(`${path}.scaleMilli`, op.scaleMilli, 500, 2000);
      }
      if (op.anchorXMilli !== undefined) {
        textOp.anchorXMilli = requireMilli(`${path}.anchorXMilli`, op.anchorXMilli, 0, 1000);
      }
      if (op.anchorYMilli !== undefined) {
        textOp.anchorYMilli = requireMilli(`${path}.anchorYMilli`, op.anchorYMilli, 0, 1000);
      }
      if (op.emphasizedTokenIds !== undefined) {
        textOp.emphasizedTokenIds = requireStringArray(
          `${path}.emphasizedTokenIds`,
          op.emphasizedTokenIds,
        );
        uniqueIds(`${path}.emphasizedTokenIds`, textOp.emphasizedTokenIds);
        const tokenSet = new Set(tokenIds);
        for (const id of textOp.emphasizedTokenIds) {
          if (!tokenSet.has(id)) {
            throw new ValidationError(
              `${path}.emphasizedTokenIds`,
              `emphasized token "${id}" is not in tokenIds`,
            );
          }
        }
      }
      return textOp;
    }
    case "sound": {
      const role = requireNonEmptyString(`${path}.role`, op.role);
      if (!SOUND_ROLES.has(role)) {
        throw new ValidationError(`${path}.role`, `invalid sound role "${role}"`);
      }
      const startTick = requireInt(`${path}.startTick`, op.startTick, 0);
      const endTick = requireInt(`${path}.endTick`, op.endTick, startTick + 1);
      return {
        kind: "sound",
        opId,
        assetId: requireNonEmptyString(`${path}.assetId`, op.assetId),
        role: role as "bed" | "sting" | "ambience" | "silence",
        startTick,
        endTick,
        volumeMilli: requireMilli(`${path}.volumeMilli`, op.volumeMilli, 0, 1000),
      };
    }
    case "cast": {
      return {
        kind: "cast",
        opId,
        slotId: requireNonEmptyString(`${path}.slotId`, op.slotId),
        variantId: requireNonEmptyString(`${path}.variantId`, op.variantId),
        showReconstructionDisclosure: requireBoolean(
          `${path}.showReconstructionDisclosure`,
          op.showReconstructionDisclosure,
        ),
      };
    }
    case "sfx": {
      const id = requireNonEmptyString(`${path}.id`, op.id);
      if (id.length > 64) {
        throw new ValidationError(`${path}.id`, "sfx id must be at most 64 characters");
      }
      return {
        kind: "sfx",
        opId,
        id,
        assetId: requireNullableString(`${path}.assetId`, op.assetId),
        atTick: requireInt(`${path}.atTick`, op.atTick, 0),
        volumeMilli: requireMilli(`${path}.volumeMilli`, op.volumeMilli, 0, 1000),
      };
    }
    case "look": {
      const treatmentId = requireNullableString(`${path}.treatmentId`, op.treatmentId);
      if (treatmentId === null) {
        if (op.intensityMilli !== undefined) {
          throw new ValidationError(
            `${path}.intensityMilli`,
            "look clear must not include intensityMilli",
          );
        }
        return { kind: "look", opId, treatmentId: null };
      }
      const lookOp: Extract<EditOp, { kind: "look" }> = {
        kind: "look",
        opId,
        treatmentId,
      };
      if (op.intensityMilli !== undefined) {
        lookOp.intensityMilli = requireMilli(
          `${path}.intensityMilli`,
          op.intensityMilli,
          0,
          1000,
        );
      }
      return lookOp;
    }
    case "motion": {
      return {
        kind: "motion",
        opId,
        beatId: requireNonEmptyString(`${path}.beatId`, op.beatId),
        presetId: requireNullableString(`${path}.presetId`, op.presetId),
      };
    }
    case "sticker": {
      const id = requireNonEmptyString(`${path}.id`, op.id);
      if (id.length > 64) {
        throw new ValidationError(`${path}.id`, "sticker id must be at most 64 characters");
      }
      const startTick = requireInt(`${path}.startTick`, op.startTick, 0);
      const endTick = requireInt(`${path}.endTick`, op.endTick, startTick + 1);
      return {
        kind: "sticker",
        opId,
        id,
        stickerId: requireNullableString(`${path}.stickerId`, op.stickerId),
        anchorXMilli: requireMilli(`${path}.anchorXMilli`, op.anchorXMilli, 0, 1000),
        anchorYMilli: requireMilli(`${path}.anchorYMilli`, op.anchorYMilli, 0, 1000),
        scaleMilli: requireMilli(`${path}.scaleMilli`, op.scaleMilli, 100, 3000),
        rotationMilli: requireMilli(`${path}.rotationMilli`, op.rotationMilli, 0, 360000),
        startTick,
        endTick,
      };
    }
    case "transition": {
      return {
        kind: "transition",
        opId,
        beatId: requireNonEmptyString(`${path}.beatId`, op.beatId),
        presetId: requireNullableString(`${path}.presetId`, op.presetId),
        durationTicks: requireInt(`${path}.durationTicks`, op.durationTicks, 1),
      };
    }
    default:
      throw new ValidationError(`${path}.kind`, `unknown kind "${kind}"`);
  }
}

export function captionMatchesGrammar(tokenIds: string[], pack: SourcePack): boolean {
  if (tokenIds.length === 0 || tokenIds.length > pack.captionGrammar.maxTokens) {
    return false;
  }
  const tokenById = new Map(pack.captionVocabulary.tokens.map((t) => [t.id, t]));
  const roles: CaptionTokenRole[] = [];
  for (const tokenId of tokenIds) {
    const token = tokenById.get(tokenId);
    if (!token) return false;
    roles.push(token.role);
  }
  return pack.captionGrammar.rules.some(
    (rule) =>
      rule.roles.length === roles.length &&
      rule.roles.every((role, i) => role === roles[i]),
  );
}

/**
 * Validates EditGraph schema and internal cursor/op consistency.
 * Accepts schema versions 1 and 2; migrates v1 → current (2).
 * Rejects unknown future versions and malformed new ops.
 */
export function validateEditGraph(raw: unknown): EditGraph {
  const root = requireObject("$", raw);

  if (!isSupportedEditGraphSchemaVersion(root.schemaVersion)) {
    throw new ValidationError(
      "schemaVersion",
      `unsupported version ${String(root.schemaVersion)}`,
    );
  }

  const opsRaw = requireArray("ops", root.ops);
  const inputSchemaVersion = root.schemaVersion as 1 | 2;
  const ops = opsRaw.map((op, i) => validateOpShape(op, i));

  if (inputSchemaVersion === EDIT_GRAPH_SCHEMA_VERSION_V1) {
    const V2_ONLY = new Set(["sfx", "look", "motion", "sticker", "transition"]);
    ops.forEach((op, i) => {
      if (V2_ONLY.has(op.kind)) {
        throw new ValidationError(
          `ops[${i}].kind`,
          `op kind "${op.kind}" is not valid in schemaVersion 1`,
        );
      }
    });
  }

  // `opId` is the op's identity, so it must be unique. `captionId` is NOT:
  // `resolveEdit` is explicitly last-op-per-captionId-wins, which is how editing
  // an existing caption (changing its framing, cycling its style) is expressed —
  // a fresh op that REPLACES the previous one rather than stacking a duplicate.
  // Rejecting that here made a graph the editor routinely builds impossible to
  // serialize, so Publish silently refused every reel whose caption had been
  // touched twice. Stickers and SFX are id-keyed the same way and were never
  // checked; captions were the leftover.
  const opIds = new Set<string>();
  ops.forEach((op, i) => {
    if (opIds.has(op.opId)) {
      throw new ValidationError(`ops[${i}].opId`, `duplicate opId "${op.opId}"`);
    }
    opIds.add(op.opId);
  });

  return migrateEditGraphToCurrent({
    schemaVersion: inputSchemaVersion,
    sourcePackId: requireNonEmptyString("sourcePackId", root.sourcePackId),
    sourcePackVersion: requireNonEmptyString("sourcePackVersion", root.sourcePackVersion),
    tickRate: requireInt("tickRate", root.tickRate, 1),
    seed: requireInt("seed", root.seed),
    ops,
    cursor: requireInt("cursor", root.cursor, 0, ops.length),
  });
}

/** Ensures edit graph references and semantics resolve against a validated source pack. */
export function validateEditGraphAgainstPack(graph: EditGraph, pack: SourcePack): void {
  if (graph.sourcePackId !== pack.id) {
    throw new ValidationError("sourcePackId", "does not match pack.id");
  }
  if (graph.sourcePackVersion !== pack.version) {
    throw new ValidationError("sourcePackVersion", "does not match pack.version");
  }
  if (graph.tickRate !== pack.tickRate) {
    throw new ValidationError("tickRate", "does not match pack.tickRate");
  }
  if (graph.schemaVersion !== EDIT_GRAPH_SCHEMA_VERSION) {
    throw new ValidationError(
      "schemaVersion",
      `expected migrated schema ${EDIT_GRAPH_SCHEMA_VERSION}`,
    );
  }

  const beatById = new Map(pack.beats.map((b) => [b.id, b]));
  const tokenIds = new Set(pack.captionVocabulary.tokens.map((t) => t.id));
  const assetById = new Map(pack.assets.map((a) => [a.id, a]));
  const lookById = new Map((pack.lookTreatments ?? []).map((t) => [t.id, t]));
  const motionById = new Map((pack.cameraMotions ?? []).map((m) => [m.id, m]));
  const stickerById = new Map((pack.stickerOptions ?? []).map((s) => [s.id, s]));
  const transitionById = new Map((pack.transitionPresets ?? []).map((t) => [t.id, t]));
  const sfxOptionsByAsset = new Map<string, string[]>();
  for (const option of pack.sfxOptions ?? []) {
    if (!option.assetId) continue;
    const list = sfxOptionsByAsset.get(option.assetId) ?? [];
    list.push(option.id);
    sfxOptionsByAsset.set(option.assetId, list);
  }
  const sourceEnd = packSourceEndTick(pack.beats);
  const canonical = pack.canonicalRecord.beatOrder;

  graph.ops.forEach((op, index) => {
    const path = `ops[${index}]`;
    switch (op.kind) {
      case "cut": {
        const beat = beatById.get(op.beatId);
        if (!beat) {
          throw new ValidationError(`${path}.beatId`, `unknown beat "${op.beatId}"`);
        }
        if (op.mode === "trim") {
          if (op.inTick === undefined || op.outTick === undefined) {
            throw new ValidationError(path, "trim requires inTick and outTick");
          }
          if (op.inTick < beat.startTick || op.outTick > beat.endTick) {
            throw new ValidationError(
              path,
              `trim bounds must lie inside beat [${beat.startTick}, ${beat.endTick})`,
            );
          }
        }
        break;
      }
      case "reorder": {
        if (op.beatOrder.length !== canonical.length) {
          throw new ValidationError(
            `${path}.beatOrder`,
            "must be a full permutation of canonical beats",
          );
        }
        uniqueIds(`${path}.beatOrder`, op.beatOrder);
        for (const beatId of op.beatOrder) {
          if (!beatById.has(beatId)) {
            throw new ValidationError(`${path}.beatOrder`, `unknown beat "${beatId}"`);
          }
        }
        for (const beatId of canonical) {
          if (!op.beatOrder.includes(beatId)) {
            throw new ValidationError(
              `${path}.beatOrder`,
              `missing canonical beat "${beatId}"`,
            );
          }
        }
        break;
      }
      case "frame": {
        const beat = beatById.get(op.beatId);
        if (!beat) {
          throw new ValidationError(`${path}.beatId`, `unknown beat "${op.beatId}"`);
        }
        if (op.subjectEntityId !== null && !beat.entityIds.includes(op.subjectEntityId)) {
          throw new ValidationError(
            `${path}.subjectEntityId`,
            `entity "${op.subjectEntityId}" is not present in beat "${op.beatId}"`,
          );
        }
        break;
      }
      case "text": {
        for (const tokenId of op.tokenIds) {
          if (!tokenIds.has(tokenId)) {
            throw new ValidationError(`${path}.tokenIds`, `unknown token "${tokenId}"`);
          }
        }
        if (!captionMatchesGrammar(op.tokenIds, pack)) {
          throw new ValidationError(
            `${path}.tokenIds`,
            "token sequence does not satisfy any caption grammar rule",
          );
        }
        if (op.endTick > sourceEnd) {
          throw new ValidationError(
            `${path}.endTick`,
            `caption timing must lie within source bounds [0, ${sourceEnd}]`,
          );
        }
        break;
      }
      case "sound": {
        const asset = assetById.get(op.assetId);
        if (!asset) {
          throw new ValidationError(`${path}.assetId`, `unknown asset "${op.assetId}"`);
        }
        if (asset.kind !== "audio") {
          throw new ValidationError(`${path}.assetId`, 'sound asset must have kind "audio"');
        }
        if (op.endTick > sourceEnd) {
          throw new ValidationError(
            `${path}.endTick`,
            `sound timing must lie within source bounds [0, ${sourceEnd}]`,
          );
        }
        break;
      }
      case "cast": {
        const slots = pack.castSlots ?? [];
        const slot = slots.find((s) => s.id === op.slotId);
        if (!slot) {
          throw new ValidationError(`${path}.slotId`, `unknown slot "${op.slotId}"`);
        }
        if (!slot.variants.some((v) => v.id === op.variantId)) {
          throw new ValidationError(
            `${path}.variantId`,
            `unknown variant "${op.variantId}"`,
          );
        }
        break;
      }
      case "sfx": {
        if (op.assetId !== null) {
          const asset = assetById.get(op.assetId);
          if (!asset) {
            throw new ValidationError(`${path}.assetId`, `unknown asset "${op.assetId}"`);
          }
          if (asset.kind !== "audio") {
            throw new ValidationError(`${path}.assetId`, 'sfx asset must have kind "audio"');
          }
          const mapped = sfxOptionsByAsset.get(op.assetId);
          if (!mapped || mapped.length === 0) {
            throw new ValidationError(
              `${path}.assetId`,
              `sfx asset "${op.assetId}" is not selectable through the pack SFX catalog`,
            );
          }
          if (mapped.length !== 1) {
            throw new ValidationError(
              `${path}.assetId`,
              `sfx asset "${op.assetId}" maps to multiple catalog options`,
            );
          }
        }
        if (op.atTick >= sourceEnd) {
          throw new ValidationError(
            `${path}.atTick`,
            `sfx timing must lie within source bounds [0, ${Math.max(0, sourceEnd - 1)}]`,
          );
        }
        break;
      }
      case "look": {
        if (op.treatmentId === null) {
          if (op.intensityMilli !== undefined) {
            throw new ValidationError(
              `${path}.intensityMilli`,
              "look clear must not include intensityMilli",
            );
          }
          break;
        }
        const treatment = lookById.get(op.treatmentId);
        if (!treatment) {
          throw new ValidationError(
            `${path}.treatmentId`,
            `unknown look treatment "${op.treatmentId}"`,
          );
        }
        if (op.intensityMilli !== undefined && !treatment.intensitySupported) {
          throw new ValidationError(
            `${path}.intensityMilli`,
            `look "${op.treatmentId}" does not support intensity`,
          );
        }
        break;
      }
      case "motion": {
        if (!beatById.has(op.beatId)) {
          throw new ValidationError(`${path}.beatId`, `unknown beat "${op.beatId}"`);
        }
        if (op.presetId === null) break;
        if (!motionById.has(op.presetId)) {
          throw new ValidationError(
            `${path}.presetId`,
            `unknown motion preset "${op.presetId}"`,
          );
        }
        break;
      }
      case "sticker": {
        if (op.stickerId !== null) {
          const sticker = stickerById.get(op.stickerId);
          if (!sticker) {
            throw new ValidationError(
              `${path}.stickerId`,
              `unknown sticker "${op.stickerId}"`,
            );
          }
          if (sticker.render.kind === "asset") {
            const asset = assetById.get(sticker.render.assetId);
            if (!asset) {
              throw new ValidationError(
                `${path}.stickerId`,
                `sticker asset "${sticker.render.assetId}" missing from pack`,
              );
            }
            if (asset.kind !== "sticker" && asset.kind !== "visual") {
              throw new ValidationError(
                `${path}.stickerId`,
                'sticker asset must have kind "sticker" or "visual"',
              );
            }
          }
          if (sticker.allowedBeatIds && sticker.allowedBeatIds.length > 0) {
            const allowedBeats = sticker.allowedBeatIds.map((beatId) => {
              const beat = beatById.get(beatId);
              if (!beat) {
                throw new ValidationError(
                  `${path}.stickerId`,
                  `sticker allowed beat "${beatId}" unknown`,
                );
              }
              return beat;
            });
            if (
              !stickerIntervalCoveredByAllowedBeats(op.startTick, op.endTick, allowedBeats)
            ) {
              throw new ValidationError(
                path,
                "sticker timing must lie entirely within allowedBeatIds intervals",
              );
            }
          }
        }
        if (op.endTick > sourceEnd) {
          throw new ValidationError(
            `${path}.endTick`,
            `sticker timing must lie within source bounds [0, ${sourceEnd}]`,
          );
        }
        break;
      }
      case "transition": {
        if (!beatById.has(op.beatId)) {
          throw new ValidationError(`${path}.beatId`, `unknown beat "${op.beatId}"`);
        }
        if (op.presetId === null) break;
        const preset = transitionById.get(op.presetId);
        if (!preset) {
          throw new ValidationError(
            `${path}.presetId`,
            `unknown transition preset "${op.presetId}"`,
          );
        }
        if (op.durationTicks > preset.maxDurationTicks) {
          throw new ValidationError(
            `${path}.durationTicks`,
            `exceeds maxDurationTicks ${preset.maxDurationTicks}`,
          );
        }
        break;
      }
    }
  });
}
